import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Video, Upload, Trash2, Eye, Play, RefreshCw, Link2, 
  Clock, HardDrive, Film, ArrowLeft, Search, Filter,
  CheckCircle, AlertCircle, Loader2, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VodManagement() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [vods, setVods] = useState([]);
  const [games, setGames] = useState([]);
  const [unassociated, setUnassociated] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal states
  const [showUpload, setShowUpload] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState(null);
  
  // Form states
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadGameId, setUploadGameId] = useState('');
  const [uploading, setUploading] = useState(false);

  // Memoize auth headers to prevent dependency changes
  const authHeaders = useMemo(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  }), [token]);

  const fetchVods = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/vod/assets`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setVods(data);
      }
    } catch (err) {
      console.error('Failed to fetch VODs:', err);
    }
  }, [authHeaders]);

  const fetchGames = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/engine/games?limit=100`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setGames(data);
      }
    } catch (err) {
      console.error('Failed to fetch games:', err);
    }
  }, [authHeaders]);

  const fetchUnassociated = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/vod/unassociated`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setUnassociated(data);
      }
    } catch (err) {
      console.error('Failed to fetch unassociated:', err);
    }
  }, [authHeaders]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/vod/stats`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [token]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchVods(), fetchGames(), fetchUnassociated(), fetchStats()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchVods, fetchGames, fetchUnassociated, fetchStats]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !uploadTitle || !uploadGameId) {
      toast.error('Please fill all required fields');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('title', uploadTitle);
    formData.append('description', uploadDescription);
    formData.append('game_id', uploadGameId);

    try {
      const res = await fetch(`${API_URL}/api/vod/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        toast.success('Video uploaded! Processing in background.');
        setShowUpload(false);
        setUploadFile(null);
        setUploadTitle('');
        setUploadDescription('');
        setUploadGameId('');
        fetchVods();
        fetchStats();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Upload failed');
      }
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleImport = async (filename) => {
    if (!uploadTitle || !uploadGameId) {
      toast.error('Please enter title and select a game');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/vod/import-recording?filename=${filename}&game_id=${uploadGameId}&title=${encodeURIComponent(uploadTitle)}&description=${encodeURIComponent(uploadDescription || '')}`, {
        method: 'POST',
        headers: authHeaders
      });

      if (res.ok) {
        toast.success('Recording imported! Processing in background.');
        setShowImport(false);
        setSelectedRecording(null);
        setUploadTitle('');
        setUploadDescription('');
        setUploadGameId('');
        fetchVods();
        fetchUnassociated();
        fetchStats();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Import failed');
      }
    } catch (err) {
      toast.error('Import failed');
    }
  };

  const handleDelete = async (vodId) => {
    if (!confirm('Are you sure you want to delete this VOD?')) return;

    try {
      const res = await fetch(`${API_URL}/api/vod/assets/${vodId}`, {
        method: 'DELETE',
        headers: authHeaders
      });

      if (res.ok) {
        toast.success('VOD deleted');
        fetchVods();
        fetchStats();
      } else {
        toast.error('Delete failed');
      }
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const handlePublish = async (vodId) => {
    try {
      const res = await fetch(`${API_URL}/api/vod/assets/${vodId}/publish`, {
        method: 'POST',
        headers: authHeaders
      });

      if (res.ok) {
        toast.success('VOD published');
        fetchVods();
      } else {
        toast.error('Publish failed');
      }
    } catch (err) {
      toast.error('Publish failed');
    }
  };

  const handleReprocess = async (vodId) => {
    try {
      const res = await fetch(`${API_URL}/api/vod/assets/${vodId}/reprocess`, {
        method: 'POST',
        headers: authHeaders
      });

      if (res.ok) {
        toast.success('Reprocessing started');
        fetchVods();
      } else {
        toast.error('Reprocess failed');
      }
    } catch (err) {
      toast.error('Reprocess failed');
    }
  };

  const formatDuration = (ms) => {
    if (!ms) return '--:--';
    const seconds = Math.floor(ms / 1000);
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ready':
        return <Badge className="bg-emerald-600"><CheckCircle className="w-3 h-3 mr-1" /> Ready</Badge>;
      case 'published':
        return <Badge className="bg-blue-600"><Eye className="w-3 h-3 mr-1" /> Published</Badge>;
      case 'processing':
        return <Badge className="bg-amber-600"><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Processing</Badge>;
      case 'error':
        return <Badge className="bg-red-600"><AlertCircle className="w-3 h-3 mr-1" /> Error</Badge>;
      default:
        return <Badge className="bg-zinc-600">{status}</Badge>;
    }
  };

  const filteredVods = vods.filter(vod => {
    const matchesSearch = !searchTerm || 
      vod.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vod.game_id?.includes(searchTerm);
    const matchesStatus = statusFilter === 'all' || vod.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-6" data-testid="vod-management">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-zinc-800 rounded-lg">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Film className="w-8 h-8 text-emerald-500" />
              VOD Pipeline
            </h1>
            <p className="text-zinc-400 mt-1">Manage video recordings and replays</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Button onClick={fetchVods} variant="outline" className="border-zinc-700">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button onClick={() => setShowUpload(true)} className="bg-emerald-600 hover:bg-emerald-500">
            <Upload className="w-4 h-4 mr-2" /> Upload Video
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
            <div className="text-2xl font-bold text-white">{stats.total_assets}</div>
            <div className="text-sm text-zinc-400">Total VODs</div>
          </div>
          <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
            <div className="text-2xl font-bold text-emerald-400">{stats.by_status?.ready || 0}</div>
            <div className="text-sm text-zinc-400">Ready</div>
          </div>
          <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
            <div className="text-2xl font-bold text-blue-400">{stats.by_status?.published || 0}</div>
            <div className="text-sm text-zinc-400">Published</div>
          </div>
          <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
            <div className="text-2xl font-bold text-amber-400">{stats.by_status?.processing || 0}</div>
            <div className="text-sm text-zinc-400">Processing</div>
          </div>
          <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-800">
            <div className="text-2xl font-bold text-white">{stats.total_storage_gb} GB</div>
            <div className="text-sm text-zinc-400">Total Storage</div>
          </div>
        </div>
      )}

      {/* Unassociated Recordings */}
      {unassociated.length > 0 && (
        <div className="bg-amber-900/20 border border-amber-700 rounded-xl p-4 mb-6">
          <h3 className="font-semibold text-amber-400 mb-3 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {unassociated.length} Unassociated Recording{unassociated.length > 1 ? 's' : ''}
          </h3>
          <div className="flex flex-wrap gap-2">
            {unassociated.slice(0, 5).map(rec => (
              <Button 
                key={rec.filename}
                variant="outline"
                className="border-amber-700 text-amber-300 hover:bg-amber-900/50"
                onClick={() => { setSelectedRecording(rec); setShowImport(true); }}
              >
                <Video className="w-4 h-4 mr-2" />
                {rec.filename.substring(0, 20)}...
                <span className="ml-2 text-xs opacity-70">{formatBytes(rec.size_bytes)}</span>
              </Button>
            ))}
            {unassociated.length > 5 && (
              <span className="text-amber-400 text-sm self-center">+{unassociated.length - 5} more</span>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by title or game ID..."
            className="pl-10 bg-zinc-900 border-zinc-700"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'processing', 'ready', 'published', 'error'].map(status => (
            <Button
              key={status}
              variant={statusFilter === status ? 'default' : 'outline'}
              className={statusFilter === status ? 'bg-emerald-600' : 'border-zinc-700'}
              onClick={() => setStatusFilter(status)}
            >
              {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {/* VOD List */}
      <div className="space-y-4">
        {filteredVods.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">
            <Video className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No VODs found</p>
          </div>
        ) : (
          filteredVods.map(vod => (
            <div key={vod.vod_id} className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden">
              <div className="flex flex-col md:flex-row">
                {/* Thumbnail */}
                <div className="w-full md:w-64 h-40 bg-zinc-800 relative shrink-0">
                  {vod.thumbnail_url ? (
                    <img 
                      src={`${API_URL}${vod.thumbnail_url}`} 
                      alt={vod.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Video className="w-12 h-12 text-zinc-600" />
                    </div>
                  )}
                  {vod.duration_ms > 0 && (
                    <div className="absolute bottom-2 right-2 bg-black/80 px-2 py-1 rounded text-xs">
                      {formatDuration(vod.duration_ms)}
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-lg">{vod.title}</h3>
                      <p className="text-sm text-zinc-400">{vod.description || 'No description'}</p>
                    </div>
                    {getStatusBadge(vod.status)}
                  </div>

                  <div className="flex flex-wrap gap-4 text-sm text-zinc-400 mb-3">
                    <span className="flex items-center gap-1">
                      <HardDrive className="w-4 h-4" /> {formatBytes(vod.file_size_bytes)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" /> {vod.views} views
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" /> {new Date(vod.created_at).toLocaleDateString()}
                    </span>
                    <span className="flex items-center gap-1 text-xs bg-zinc-800 px-2 py-1 rounded">
                      Game: {vod.game_id?.substring(0, 8)}...
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    {vod.video_url && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-zinc-700"
                        onClick={() => window.open(`${API_URL}${vod.video_url}`, '_blank')}
                      >
                        <Play className="w-4 h-4 mr-1" /> Play
                      </Button>
                    )}
                    {vod.status === 'ready' && (
                      <Button 
                        size="sm" 
                        className="bg-blue-600"
                        onClick={() => handlePublish(vod.vod_id)}
                      >
                        <Eye className="w-4 h-4 mr-1" /> Publish
                      </Button>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="border-zinc-700"
                      onClick={() => handleReprocess(vod.vod_id)}
                    >
                      <RefreshCw className="w-4 h-4 mr-1" /> Reprocess
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="border-red-700 text-red-400 hover:bg-red-900/50"
                      onClick={() => handleDelete(vod.vod_id)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl border border-zinc-700 w-full max-w-lg">
            <div className="px-6 py-4 border-b border-zinc-700 flex items-center justify-between">
              <h2 className="text-xl font-bold">Upload Video</h2>
              <button onClick={() => setShowUpload(false)} className="p-2 hover:bg-zinc-800 rounded-full">
                ✕
              </button>
            </div>
            <form onSubmit={handleUpload} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Video File *</label>
                <Input
                  type="file"
                  accept="video/*"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  className="bg-zinc-800 border-zinc-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Title *</label>
                <Input
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  placeholder="Game title or description"
                  className="bg-zinc-800 border-zinc-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Description</label>
                <Input
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder="Optional description"
                  className="bg-zinc-800 border-zinc-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Associate with Game *</label>
                <select
                  value={uploadGameId}
                  onChange={(e) => setUploadGameId(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-600 rounded-md px-3 py-2 text-white"
                >
                  <option value="">Select a game...</option>
                  {games.map(game => (
                    <option key={game.game_id} value={game.game_id}>
                      {game.home_team_name} vs {game.away_team_name} ({game.status})
                    </option>
                  ))}
                </select>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-emerald-600 hover:bg-emerald-500"
                disabled={uploading}
              >
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {uploading ? 'Uploading...' : 'Upload Video'}
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImport && selectedRecording && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl border border-zinc-700 w-full max-w-lg">
            <div className="px-6 py-4 border-b border-zinc-700 flex items-center justify-between">
              <h2 className="text-xl font-bold">Import Recording</h2>
              <button onClick={() => { setShowImport(false); setSelectedRecording(null); }} className="p-2 hover:bg-zinc-800 rounded-full">
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-zinc-800 rounded-lg p-4">
                <div className="text-sm text-zinc-400">File</div>
                <div className="font-mono text-emerald-400">{selectedRecording.filename}</div>
                <div className="text-sm text-zinc-500 mt-1">
                  {formatBytes(selectedRecording.size_bytes)} • {formatDuration(selectedRecording.duration_ms)}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Title *</label>
                <Input
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  placeholder="Game title or description"
                  className="bg-zinc-800 border-zinc-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Description</label>
                <Input
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder="Optional description"
                  className="bg-zinc-800 border-zinc-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Associate with Game *</label>
                <select
                  value={uploadGameId}
                  onChange={(e) => setUploadGameId(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-600 rounded-md px-3 py-2 text-white"
                >
                  <option value="">Select a game...</option>
                  {games.map(game => (
                    <option key={game.game_id} value={game.game_id}>
                      {game.home_team_name} vs {game.away_team_name} ({game.status})
                    </option>
                  ))}
                </select>
              </div>
              <Button 
                onClick={() => handleImport(selectedRecording.filename)}
                className="w-full bg-emerald-600 hover:bg-emerald-500"
              >
                <Link2 className="w-4 h-4 mr-2" /> Import & Process
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
